<template>
  <div
    v-if="isOpen"
    class="alfred"
    @keydown.esc.prevent="handleEsc"
  >
    <button
      aria-label="Close command palette"
      class="alfred__backdrop"
      @click="close"
    />

    <section
      aria-label="Command palette"
      aria-modal="true"
      class="alfred__panel"
      role="dialog"
    >
      <header class="alfred__header">
        <button
          v-if="mode === 'goto' || mode === 'search' || mode === 'impersonate'"
          aria-label="Back"
          class="alfred__back"
          @click="mode === 'goto' ? exitGoTo() : mode === 'impersonate' ? exitImpersonate() : exitSearch()"
        >
          <FontAwesomeIcon
            :icon="['fas', 'chevron-left']"
            fixed-width
          />
        </button>

        <FontAwesomeIcon
          v-else
          :icon="['fas', 'search']"
          class="alfred__search-icon"
          fixed-width
        />

        <span
          v-if="mode === 'goto' || mode === 'search' || mode === 'impersonate'"
          class="alfred__breadcrumb"
        >
          {{ mode === 'goto' ? 'Go To' : mode === 'impersonate' ? 'Impersonate' : 'Search' }}
        </span>

        <span
          v-if="mode === 'search' && searchFilteredResource"
          class="alfred__resource-tag"
        >
          {{ searchFilteredResource }}
        </span>

        <input
          ref="queryInput"
          v-model.trim="query"
          autocomplete="off"
          class="alfred__input"
          :placeholder="mode === 'goto' ? 'Search destinations…' : mode === 'search' ? 'Search OpenFreight…' : mode === 'impersonate' ? 'Search teams to impersonate…' : 'Type a command or search'"
          type="text"
          @keydown.backspace="handleBackspaceOnEmpty"
          @keydown.down.prevent="move(1)"
          @keydown.enter.prevent="triggerSelected"
          @keydown.up.prevent="move(-1)"
          @keydown.esc.prevent="handleEsc"
        >

        <kbd class="alfred__hint">Esc</kbd>
      </header>

      <ul
        v-if="filteredActions.length"
        aria-label="Alfred actions"
        class="alfred__list"
        role="listbox"
      >
        <li
          v-for="(action, index) in filteredActions"
          :key="actionKey(action, index)"
          ref="actionItems"
          :ref-in-for="true"
          :aria-selected="index === selectedIndex"
          :class="{ 'is-active': index === selectedIndex }"
          class="alfred__item"
          role="option"
          @click="triggerAction(action)"
          @mouseenter="selectedIndex = index"
        >
          <div class="alfred__item-title">
            {{ getActionTitle(action) }}
          </div>

          <div
            v-if="getActionSubtitle(action)"
            class="alfred__item-subtitle"
          >
            {{ getActionSubtitle(action) }}
          </div>
        </li>
      </ul>

      <div
        v-else
        class="alfred__empty"
      >
        {{ (mode === 'search' || mode === 'impersonate') && isSearching ? 'Searching…' : (mode === 'search' || mode === 'impersonate') && query ? 'No results found' : 'No matching actions' }}
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data () {
    return {
      isOpen: false,
      mode: 'root',
      query: '',
      selectedIndex: 0,
      searchResults: [],
      isSearching: false,
      searchAbortController: undefined,
      searchFilteredResource: null,
      originalMousetrapStopCallback: null,
      alfredMousetrapStopCallback: null
    }
  },
  computed: {
    goToRoutes () {
      return this.getAllApplicationRoutes().map(({ routeName, label, breadcrumb }) => ({
        name: label,
        description: breadcrumb,
        routeName,
        keywords: ['go to', 'navigate', routeName, label, breadcrumb]
      }))
    },
    actions () {
      if (this.mode === 'goto') {
        return this.goToRoutes
      }

      if (this.mode === 'search' || this.mode === 'impersonate') {
        return this.searchResults
      }

      const dynamicActions = this.$alfred.getActions() || []
      const visibleDynamicActions = Array.isArray(dynamicActions)
        ? dynamicActions.filter(action => !action.hidden)
        : []

      return [
        { name: 'Go To', description: 'Navigate to a section', _goTo: true },
        { name: 'Search', description: 'Search OpenFreight', _search: true },
        { name: 'Impersonate', description: 'Impersonate a team', _impersonate: true },
        ...visibleDynamicActions
      ]
    },

    filteredActions () {
      if (this.mode === 'search') {
        return this.actions
      }

      const term = this.query.toLowerCase()

      if (!term) {
        return this.actions
      }

      return this.actions.filter((action) => {
        const title = this.getActionTitle(action).toLowerCase()
        const subtitle = this.getActionSubtitle(action).toLowerCase()
        const keywords = this.getActionKeywords(action).join(' ').toLowerCase()

        return title.includes(term) || subtitle.includes(term) || keywords.includes(term)
      })
    }
  },
  watch: {
    query () {
      this.selectedIndex = 0

      if (this.mode === 'search') {
        const resourceMap = {
          a: 'Accounts',
          s: 'Carriers',
          p: 'Pickups',
          u: 'Profiles',
          t: 'Teams',
          c: 'Consignments'
        }
        const resourceFilter = this.query && this.query[1] === '/' ? this.query[0] : null
        this.searchFilteredResource = resourceFilter ? resourceMap[resourceFilter] || null : null

        clearTimeout(this._searchDebounceTimer)
        this._searchDebounceTimer = setTimeout(() => {
          this.performSearch(this.query)
        }, 200)
      }

      if (this.mode === 'impersonate') {
        clearTimeout(this._searchDebounceTimer)
        this._searchDebounceTimer = setTimeout(() => {
          this.performImpersonateSearch(this.query)
        }, 200)
      }
    },
    filteredActions () {
      if (!this.filteredActions.length) {
        this.selectedIndex = 0
        return
      }

      if (this.selectedIndex > this.filteredActions.length - 1) {
        this.selectedIndex = this.filteredActions.length - 1
      }

      this.$nextTick(() => {
        this.scrollSelectedIntoView()
      })
    }
  },
  mounted () {
    this.enableGlobalAlfredHotkeys()
    this.$mousetrap.bind(['command+j', 'ctrl+j'], this.toggleFromHotkey)
    window.addEventListener('alfred:open', this.handleExternalOpen)
  },
  beforeDestroy () {
    this.$mousetrap.unbind(['command+j', 'ctrl+j'])
    this.disableGlobalAlfredHotkeys()
    window.removeEventListener('alfred:open', this.handleExternalOpen)
  },
  methods: {
    enableGlobalAlfredHotkeys () {
      if (typeof this.alfredMousetrapStopCallback === 'function') {
        return
      }

      this.originalMousetrapStopCallback = this.$mousetrap.stopCallback
      const globalCombos = ['command+j', 'ctrl+j']

      this.alfredMousetrapStopCallback = (event, element, combo) => {
        if (combo && globalCombos.includes(combo)) {
          return false
        }

        return this.originalMousetrapStopCallback
          ? this.originalMousetrapStopCallback(event, element, combo)
          : false
      }

      this.$mousetrap.stopCallback = this.alfredMousetrapStopCallback
    },
    disableGlobalAlfredHotkeys () {
      if (this.$mousetrap.stopCallback === this.alfredMousetrapStopCallback && typeof this.originalMousetrapStopCallback === 'function') {
        this.$mousetrap.stopCallback = this.originalMousetrapStopCallback
      }

      this.originalMousetrapStopCallback = null
      this.alfredMousetrapStopCallback = null
    },
    handleExternalOpen () {
      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.$refs.queryInput) {
            this.$refs.queryInput.focus()
          }
        })
        return
      }

      this.open()
    },
    toggleFromHotkey (event) {
      if (event) {
        event.preventDefault()
      }

      this.isOpen ? this.close() : this.open()
    },
    open () {
      this.isOpen = true
      this.selectedIndex = 0

      this.$nextTick(() => {
        if (this.$refs.queryInput) {
          this.$refs.queryInput.focus()
        }
      })
    },
    close () {
      this.isOpen = false
      this.mode = 'root'
      this.query = ''
      this.selectedIndex = 0
      this.searchResults = []

      if (this.searchAbortController) {
        this.searchAbortController.abort()
        this.searchAbortController = undefined
      }
    },
    enterImpersonate () {
      this.mode = 'impersonate'
      this.query = ''
      this.searchResults = []
      this.selectedIndex = 0

      this.$nextTick(() => {
        if (this.$refs.queryInput) {
          this.$refs.queryInput.focus()
        }
      })
    },
    exitImpersonate () {
      if (this.searchAbortController) {
        this.searchAbortController.abort()
        this.searchAbortController = undefined
      }

      this.mode = 'root'
      this.query = ''
      this.searchResults = []
      this.selectedIndex = 0

      this.$nextTick(() => {
        if (this.$refs.queryInput) {
          this.$refs.queryInput.focus()
        }
      })
    },
    async performImpersonateSearch (query) {
      if (!query || !query.trim()) {
        this.searchResults = []
        this.isSearching = false
        return
      }

      if (this.searchAbortController) {
        this.searchAbortController.abort()
      }

      this.searchAbortController = new AbortController()
      this.isSearching = true

      try {
        const isNewSearch = this.$store.getters['configuration/search/isNewSearch']
        const resultsPerPage = this.$store.getters['configuration/search/resultsPerPage']
        const allSearchable = this.$store.getters['configuration/search/searchable']
        const teamsSearchable = allSearchable.filter(s => s.toLowerCase().includes('team'))

        const results = await this.$sdk.search(
          query,
          teamsSearchable.join(','),
          undefined,
          this.searchAbortController.signal,
          isNewSearch,
          resultsPerPage
        )

        this.searchResults = results.map(item => this.mapSearchResultToAction(item))
      } catch {
        // Aborted or error — leave results as-is
      } finally {
        this.isSearching = false
        this.searchAbortController = undefined
      }
    },
    exitGoTo () {
      this.mode = 'root'
      this.query = ''
      this.selectedIndex = 0

      this.$nextTick(() => {
        if (this.$refs.queryInput) {
          this.$refs.queryInput.focus()
        }
      })
    },
    enterSearch () {
      this.mode = 'search'
      this.query = ''
      this.searchResults = []
      this.searchFilteredResource = null
      this.selectedIndex = 0

      this.$nextTick(() => {
        if (this.$refs.queryInput) {
          this.$refs.queryInput.focus()
        }
      })
    },
    exitSearch () {
      if (this.searchAbortController) {
        this.searchAbortController.abort()
        this.searchAbortController = undefined
      }

      this.mode = 'root'
      this.query = ''
      this.searchResults = []
      this.searchFilteredResource = null
      this.selectedIndex = 0

      this.$nextTick(() => {
        if (this.$refs.queryInput) {
          this.$refs.queryInput.focus()
        }
      })
    },
    async performSearch (query) {
      const resourcePrefixMatch = query && query[1] === '/'
      const rawQuery = resourcePrefixMatch ? query.slice(2) : query

      if (!rawQuery || !rawQuery.trim()) {
        this.searchResults = []
        this.isSearching = false
        return
      }

      if (this.searchAbortController) {
        this.searchAbortController.abort()
      }

      this.searchAbortController = new AbortController()
      this.isSearching = true

      try {
        const allSearchable = this.$store.getters['configuration/search/searchable']
        const isNewSearch = this.$store.getters['configuration/search/isNewSearch']
        const resultsPerPage = this.$store.getters['configuration/search/resultsPerPage']

        const resourceTypeMap = {
          a: 'account',
          s: 'carrier',
          p: 'pickup',
          u: 'profile',
          t: 'team',
          c: 'consignment'
        }
        const prefix = query && query[1] === '/' ? query[0] : null
        const filteredType = prefix ? resourceTypeMap[prefix] : null
        const searchable = filteredType
          ? allSearchable.filter(s => s.toLowerCase().includes(filteredType))
          : allSearchable

        const results = await this.$sdk.search(
          rawQuery,
          searchable.join(','),
          undefined,
          this.searchAbortController.signal,
          isNewSearch,
          resultsPerPage
        )

        this.searchResults = results.map(item => this.mapSearchResultToAction(item))
      } catch {
        // Aborted or error — leave results as-is
      } finally {
        this.isSearching = false
        this.searchAbortController = undefined
      }
    },
    mapSearchResultToAction (item) {
      const type = item.constructor.name

      const typeLabels = {
        account: 'Account',
        carrier: 'Carrier',
        pickup: 'Pickup',
        profile: 'Profile',
        consignment: 'Consignment',
        account_team: 'Team',
        team: 'Team'
      }

      const titles = {
        account: this.$_.get(item, 'name') || '-',
        carrier: this.$_.get(item, 'name') || '-',
        pickup: this.$_.get(item, 'booking_reference') || '-',
        profile: this.$_.get(item, 'name_full') || '-',
        consignment: this.$_.get(item, 'con_full') || '-',
        account_team: this.$_.get(item, 'name') || '-',
        team: this.$_.get(item, 'name') || '-'
      }

      return {
        name: titles[type] || '-',
        description: typeLabels[type] || type,
        _searchResult: item
      }
    },
    navigateToResult (item) {
      const type = item.constructor.name

      if (type === 'account') {
        this.$router.push({ name: 'customerservice-accounts-account', params: { accountId: item.id } })
      } else if (type === 'carrier') {
        this.$router.push({ name: 'manage-carriers-carrier', params: { carrierId: item.id } })
      } else if (type === 'pickup') {
        this.$router.push({ name: 'customerservice-pickups-pickup', params: { pickupId: item.id } })
      } else if (type === 'consignment') {
        this.$router.push({ name: 'freight-consignments-v3-consignment', params: { consignmentId: item.id } })
      } else if (type === 'profile') {
        this.$router.push({ name: 'manage-users-user-profile', params: { userId: item.id } })
      } else if (type === 'account_team' || type === 'team') {
        this.$sdk.team.read(item.id, b => { b.include = 'account' }).then(res => {
          this.$router.push({ name: 'manage-accounts-account-configurations-teams-team', params: { accountId: res.account.id, teamId: item.id } })
        })
      }

      this.close()
    },
    handleEsc () {
      if (this.mode === 'goto') {
        this.exitGoTo()
      } else if (this.mode === 'search') {
        this.exitSearch()
      } else if (this.mode === 'impersonate') {
        this.exitImpersonate()
      } else {
        this.close()
      }
    },
    handleBackspaceOnEmpty (event) {
      if ((this.mode === 'goto' || this.mode === 'search' || this.mode === 'impersonate') && !this.query) {
        event.preventDefault()
        if (this.mode === 'goto') {
          this.exitGoTo()
        } else if (this.mode === 'search') {
          this.exitSearch()
        } else if (this.mode === 'impersonate') {
          this.exitImpersonate()
        }
      }
    },
    async impersonateResult (item) {
      this.$message.info('Attempting to impersonate team…')
      const doImpersonate = async (accountId, teamId) => {
        this.$sdk.account_team.impersonate(
          accountId,
          (await this.$session.instance).tenancy.id,
          () => {
            this.$message.success('Impersonation Token Generated. Redirecting to Open360...')
          }
        )
        this.close()
      }

      if (item.account && item.account.slug) {
        try {
          doImpersonate(item.account.slug, item.id)
        } catch (error) {
          this.$message.error('Failed to impersonate team')
        }
      } else {
        this.$sdk.team.read(item.id, b => { b.include = 'account' }).then(res => {
          try {
            doImpersonate(res.account.slug, item.id)
          } catch (error) {
            this.$message.error('Failed to impersonate team')
          }
        })
      }
    },
    move (delta) {
      if (!this.filteredActions.length) {
        return
      }

      const length = this.filteredActions.length
      this.selectedIndex = (this.selectedIndex + delta + length) % length

      this.$nextTick(() => {
        this.scrollSelectedIntoView()
      })
    },
    scrollSelectedIntoView () {
      const items = this.$refs.actionItems

      if (!items || !items.length) {
        return
      }

      const selectedItem = items[this.selectedIndex]

      if (selectedItem && typeof selectedItem.scrollIntoView === 'function') {
        selectedItem.scrollIntoView({ block: 'nearest' })
      }
    },
    triggerSelected () {
      const action = this.filteredActions[this.selectedIndex]

      if (!action) {
        return
      }

      this.triggerAction(action)
    },
    triggerAction (action) {
      const payload = {
        query: this.query,
        action
      }

      if (action._goTo) {
        this.mode = 'goto'
        this.query = ''
        this.selectedIndex = 0

        this.$nextTick(() => {
          if (this.$refs.queryInput) {
            this.$refs.queryInput.focus()
          }
        })
        return
      }

      if (action._search) {
        this.enterSearch()
        return
      }

      if (action._impersonate) {
        this.enterImpersonate()
        return
      }

      if (action._searchResult) {
        if (this.mode === 'impersonate') {
          this.impersonateResult(action._searchResult)
        } else {
          this.navigateToResult(action._searchResult)
        }
        return
      }

      if (action.routeName) {
        this.$router.push({ name: action.routeName })
      } else if (typeof action.handler === 'function') {
        action.handler(payload)
      } else if (action.name) {
        this.$alfred.triggerAction(action.name, payload)
      }

      this.close()
    },
    getAllApplicationRoutes () {
      const rootRoutes = this.$router && this.$router.options && this.$router.options.routes

      if (!Array.isArray(rootRoutes)) {
        return []
      }

      const appRoute = rootRoutes.find(route => route.name === 'application')

      if (!appRoute || !Array.isArray(appRoute.children)) {
        return []
      }

      const results = []

      const traverse = (routes, parents) => {
        routes.forEach((route) => {
          if (!route || !route.name || !route.path) {
            return
          }

          if (route.path.includes(':')) {
            if (Array.isArray(route.children)) {
              traverse(route.children, parents)
            }
            return
          }

          if (route.showInAlfred) {
            const segmentLabel = route.alfredTitle || this.getRouteTitle(route)
            const chain = [...parents, segmentLabel]
            const breadcrumb = chain.join(' → ')

            results.push({
              routeName: route.name,
              label: segmentLabel,
              breadcrumb
            })
          }

          if (Array.isArray(route.children)) {
            const segmentLabel = route.alfredTitle || this.getRouteTitle(route)
            traverse(route.children, route.showInAlfred ? [...parents, segmentLabel] : parents)
          }
        })
      }

      traverse(appRoute.children, [])

      return results
    },
    getRouteTitle (route) {
      if (route.meta && route.meta.title) {
        return route.meta.title
      }

      const source = route.path || route.name || 'Route'

      return source
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
    },
    actionKey (action, index) {
      return `${action.name || this.getActionTitle(action)}-${index}`
    },
    getActionTitle (action) {
      return action.title || action.label || action.name || 'Untitled action'
    },
    getActionSubtitle (action) {
      return action.description || action.group || ''
    },
    getActionKeywords (action) {
      if (Array.isArray(action.keywords)) {
        return action.keywords
      }

      if (typeof action.keywords === 'string') {
        return [action.keywords]
      }

      return []
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'variables';

.alfred {
  position: fixed;
  inset: 0;
  z-index: 5000;
}

.alfred__backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: rgba(24, 31, 41, 0.55);
  backdrop-filter: blur(4px);
  cursor: default;
}

.alfred__panel {
  position: relative;
  width: calc(100vw - 24px);
  max-width: 760px;
  max-height: 75vh;
  min-height: 0;
  margin: 11vh auto 0;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: #ffffff;
  box-shadow: 0 28px 58px rgba(24, 31, 41, 0.36);
  overflow: hidden;
}

.alfred__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #eef1f6;
}

.alfred__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: #f8fafc;
  color: $dark;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #edf4fc;
    border-color: $primary;
    color: $primary;
  }
}

.alfred__breadcrumb {
  padding: 2px 8px;
  border-radius: 5px;
  background: #edf4fc;
  color: $primary;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.alfred__resource-tag {
  padding: 2px 8px;
  border-radius: 5px;
  background: #202c38;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.alfred__search-icon {
  color: $dark;
  opacity: 0.75;
}

.alfred__input {
  width: 100%;
  border: 0;
  outline: 0;
  font-size: 16px;
  line-height: 1.3;
  color: #202c38;
  background: transparent;
}

.alfred__hint {
  border: 1px solid #d7dde8;
  border-bottom-width: 2px;
  border-radius: 5px;
  padding: 2px 7px;
  font-size: 11px;
  font-family: $font-family-monospace;
  color: #5f6b7a;
  background: #f8fafc;
}

.alfred__list {
  list-style: none;
  margin: 0;
  padding: 8px;
  max-height: 60vh;
  overflow: auto;
}

.alfred__item {
  padding: 10px 12px;
  border-radius: 9px;
  cursor: pointer;
  transition: background-color 120ms ease;
}

.alfred__item.is-active,
.alfred__item:hover {
  background: #edf4fc;
}

.alfred__item-title {
  color: #202c38;
  font-size: 14px;
  font-weight: 600;
}

.alfred__item-subtitle {
  margin-top: 4px;
  color: #768291;
  font-size: 12px;
}

.alfred__empty {
  padding: 20px 16px;
  color: #6f7b8a;
  font-size: 13px;
}

@media (max-width: 767px) {
  .alfred__panel {
    margin-top: 7vh;
  }
}
</style>
